// src/components/CartPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// SVG Icons for Cart Page
const CartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const HeartIcon = ({ filled = false }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://shop.neoteeforge.com/neotee-backend';

const getTokenFromStorage = () => {
  const possibleKeys = [
    'neoteeforge_auth_token',
    'token',
    'auth_token',
    'neotee_auth'
  ];
  
  for (const key of possibleKeys) {
    const value = localStorage.getItem(key);
    if (value) {
      if (key === 'neotee_auth') {
        try {
          const authData = JSON.parse(value);
          if (authData.token) {
            return authData.token;
          }
        } catch (e) {
          console.error('Error parsing legacy auth data:', e);
        }
      }
      return value;
    }
  }
  
  return null;
};

const CartPage = ({ 
  auth, 
  cartItems, 
  onUpdateQuantity, 
  onRemoveItem, 
  currency, 
  currencyData, 
  exchangeRates,
  convertPrice,
  getPriceBreakdown,
  onCheckout,
  getPrimaryImage
}) => {
  const navigate = useNavigate();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loading, setLoading] = useState({});
  const [productDetails, setProductDetails] = useState({});
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Load product details from backend to get accurate pricing
  useEffect(() => {
    const loadProductDetails = async () => {
      const details = {};
      for (const item of cartItems) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/products/${item.id}`);
          if (response.ok) {
            const data = await response.json();
            details[item.id] = data.product || data;
          } else {
            details[item.id] = item;
          }
        } catch (error) {
          console.error(`Error loading product ${item.id}:`, error);
          details[item.id] = item;
        }
      }
      setProductDetails(details);
    };

    if (cartItems.length > 0) {
      loadProductDetails();
    }
  }, [cartItems]);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Get enhanced product with complete pricing data - EXACT LOGIC FROM ADMIN DASHBOARD
  const getEnhancedProduct = (item) => {
    const details = productDetails[item.id];
    if (!details) return item;
    
    // Extract pricing data from backend
    const mrp = parseFloat(details.mrp_price || details.mrp || details.price || item.price || 113);
    const salePrice = parseFloat(details.sale_price || details.selling_price || 0);
    const defaultDiscount = parseFloat(details.default_discount || 0);
    const couponDiscount = parseFloat(details.coupon_discount || 0);
    const gstIncluded = details.gst_included === 1 || details.gst_included === true;
    
    // Calculate base price - use sale price if available, otherwise MRP
    const basePrice = salePrice > 0 ? salePrice : mrp;
    
    // Determine effective discount - use the higher one
    const effectiveDiscount = Math.max(defaultDiscount, couponDiscount);
    
    // Apply discount
    const discountedPrice = basePrice - (basePrice * effectiveDiscount / 100);
    
    // Apply GST if not included (18% GST)
    let finalPrice = discountedPrice;
    let gstAmount = 0;
    if (!gstIncluded) {
      gstAmount = discountedPrice * 0.18; // 18% GST
      finalPrice = discountedPrice + gstAmount;
    }
    
    return {
      ...item,
      ...details,
      mrp,
      salePrice,
      basePrice,
      defaultDiscount,
      couponDiscount,
      effectiveDiscount,
      discountedPrice,
      gstIncluded,
      gstAmount,
      finalPrice,
      savings: mrp - finalPrice,
      savingsPercentage: mrp > 0 ? ((mrp - finalPrice) / mrp * 100).toFixed(2) : 0
    };
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalGst = 0;
    let totalMrp = 0;
    let totalSavings = 0;

    cartItems.forEach(item => {
      const enhancedItem = getEnhancedProduct(item);
      const quantity = item.quantity || 1;
      
      subtotal += enhancedItem.finalPrice * quantity;
      totalGst += enhancedItem.gstAmount * quantity;
      totalMrp += enhancedItem.mrp * quantity;
    });

    totalSavings = totalMrp - subtotal;

    return {
      subtotal,
      gst: totalGst,
      total: subtotal,
      mrp: totalMrp,
      savings: totalSavings
    };
  };

  const totals = calculateTotals();

  // Get price breakdown for individual items
  const getItemPriceBreakdown = (item) => {
    const enhancedItem = getEnhancedProduct(item);
    const quantity = item.quantity || 1;
    
    return {
      mrp: enhancedItem.mrp * quantity,
      salePrice: enhancedItem.salePrice * quantity,
      basePrice: enhancedItem.basePrice * quantity,
      effectiveDiscount: enhancedItem.effectiveDiscount,
      discountedPrice: enhancedItem.discountedPrice * quantity,
      gstAmount: enhancedItem.gstAmount * quantity,
      finalPrice: enhancedItem.finalPrice * quantity,
      savings: enhancedItem.savings * quantity,
      savingsPercentage: enhancedItem.savingsPercentage,
      gstIncluded: enhancedItem.gstIncluded,
      perItem: {
        mrp: enhancedItem.mrp,
        salePrice: enhancedItem.salePrice,
        basePrice: enhancedItem.basePrice,
        discountedPrice: enhancedItem.discountedPrice,
        gstAmount: enhancedItem.gstAmount,
        finalPrice: enhancedItem.finalPrice
      }
    };
  };

  // Update quantity
  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setLoading(prev => ({ ...prev, [itemId]: true }));
    
    try {
      onUpdateQuantity(itemId, newQuantity);
      showNotification('Cart updated successfully');
    } catch (error) {
      console.error('Update quantity error:', error);
      showNotification('Failed to update cart', 'error');
    } finally {
      setLoading(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Remove item
  const handleRemoveItem = async (itemId) => {
    setLoading(prev => ({ ...prev, [itemId]: true }));
    
    try {
      onRemoveItem(itemId);
      showNotification('Item removed from cart');
    } catch (error) {
      console.error('Remove item error:', error);
      showNotification('Failed to remove item', 'error');
    } finally {
      setLoading(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Handle buy now
  const handleBuyNow = (item) => {
    if (!auth) {
      setShowLoginPrompt(true);
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    
    const enhancedItem = getEnhancedProduct(item);
    localStorage.setItem('direct_purchase', JSON.stringify({
      ...enhancedItem,
      quantity: 1
    }));
    navigate('/checkout', { state: { directPurchase: enhancedItem } });
  };

  // Handle proceed to checkout
  const handleProceedToCheckout = () => {
    if (!auth) {
      setShowLoginPrompt(true);
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    
    if (cartItems.length === 0) {
      showNotification('Your cart is empty', 'error');
      return;
    }
    
    navigate('/checkout');
  };

  // Handle continue shopping
  const handleContinueShopping = () => {
    navigate('/');
  };

  // Handle product click
  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  // Clear entire cart
  const handleClearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your cart?')) return;
    
    setLoading(prev => ({ ...prev, clear: true }));
    
    try {
      cartItems.forEach(item => onRemoveItem(item.id));
      showNotification('Cart cleared successfully');
    } catch (error) {
      console.error('Clear cart error:', error);
      showNotification('Failed to clear cart', 'error');
    } finally {
      setLoading(prev => ({ ...prev, clear: false }));
    }
  };

  // Get item image
  const getItemImage = (item) => {
    if (getPrimaryImage) {
      return getPrimaryImage(item);
    }
    return item.thumbnail_url || item.image || 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=200&h=300&fit=crop';
  };

  // Format price with currency
  const formatPrice = (price) => {
    if (typeof convertPrice === 'function') {
      return convertPrice(price);
    }
    const symbol = currencyData[currency]?.symbol || '₹';
    return `${symbol}${price.toFixed(2)}`;
  };

  // Empty cart state
  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        {notification.show && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
          </div>
        )}

        <style>{`
          .cart-page {
            min-height: 100vh;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            padding: 20px;
            margin-top: 80px;
            position: relative;
          }
          @media (max-width: 768px) {
            .cart-page { margin-top: 70px; padding: 15px; }
          }
          .notification {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 20px;
            border-radius: 8px;
            color: white;
            font-weight: 600;
            z-index: 1000;
            animation: slideIn 0.3s ease;
          }
          .notification.success { background: linear-gradient(135deg, #10b981, #059669); }
          .notification.error { background: linear-gradient(135deg, #ef4444, #dc2626); }
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          .cart-empty-state {
            text-align: center;
            padding: 3rem 1rem;
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
            max-width: 500px;
            margin: 2rem auto;
          }
          .empty-icon { margin-bottom: 1rem; color: #64748b; }
          .empty-title {
            font-family: 'Playfair Display', serif;
            color: #334155;
            margin-bottom: 1rem;
            font-size: 1.5rem;
          }
          .empty-message {
            color: #64748b;
            margin-bottom: 2rem;
            font-size: 0.95rem;
            line-height: 1.6;
          }
          .action-buttons {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            max-width: 300px;
            margin: 0 auto;
          }
          @media (min-width: 640px) {
            .action-buttons { flex-direction: row; justify-content: center; }
          }
          .btn {
            padding: 1rem 2rem;
            border-radius: 12px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            border: none;
          }
          .btn-primary {
            background: linear-gradient(135deg, #e63946, #d00000);
            color: white;
          }
          .btn-primary:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(230, 57, 70, 0.3);
          }
          .btn-outline {
            background: transparent;
            color: #e63946;
            border: 2px solid #e63946;
          }
          .btn-outline:hover:not(:disabled) {
            background: #e63946;
            color: white;
            transform: translateY(-2px);
          }
          .spinner {
            width: 16px;
            height: 16px;
            border: 2px solid #f3f3f3;
            border-top: 2px solid #e63946;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}</style>

        <div className="cart-empty-state">
          <div className="empty-icon"><CartIcon /></div>
          <h2 className="empty-title">Your Cart is Empty</h2>
          <p className="empty-message">
            Discover amazing books and add them to your cart. Your literary journey awaits!
          </p>
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleContinueShopping}>
              <HomeIcon /> Continue Shopping
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/wishlist')}>
              <HeartIcon /> View Wishlist
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <style>{`
        .cart-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 20px;
          margin-top: 80px;
          position: relative;
        }
        @media (max-width: 768px) { .cart-page { margin-top: 70px; padding: 15px; } }
        @media (max-width: 480px) { .cart-page { padding: 10px; } }

        .notification {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 20px;
          border-radius: 8px;
          color: white;
          font-weight: 600;
          z-index: 1000;
          animation: slideIn 0.3s ease;
        }
        .notification.success { background: linear-gradient(135deg, #10b981, #059669); }
        .notification.error { background: linear-gradient(135deg, #ef4444, #dc2626); }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .cart-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          border: none;
        }
        .back-button:hover:not(:disabled) {
          background: #f1f5f9;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .cart-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.25rem;
          background: linear-gradient(135deg, #334155, #e63946);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
          line-height: 1.2;
          flex: 1;
        }
        @media (max-width: 768px) { .cart-title { font-size: 1.75rem; } }

        .refresh-btn, .clear-cart-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
        }
        .refresh-btn { background: #f8fafc; color: #64748b; }
        .clear-cart-btn { background: #fee2e2; color: #dc2626; border: 1px solid #fecaca; }
        .refresh-btn:hover:not(:disabled) { background: #f1f5f9; transform: translateY(-1px); }
        .clear-cart-btn:hover:not(:disabled) { background: #fecaca; transform: translateY(-1px); }

        .cart-layout {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          width: 100%;
        }
        @media (min-width: 1024px) {
          .cart-layout { flex-direction: row; gap: 2rem; align-items: flex-start; }
        }

        .cart-items-section {
          background: white;
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: 1px solid #f1f5f9;
          width: 100%;
        }
        @media (min-width: 1024px) { .cart-items-section { flex: 1; } }
        @media (max-width: 768px) { .cart-items-section { padding: 1.5rem; } }
        @media (max-width: 480px) { .cart-items-section { padding: 1rem; } }

        .cart-item {
          display: flex;
          gap: 1.5rem;
          padding: 1.5rem 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .cart-item:last-child { border-bottom: none; }
        @media (max-width: 768px) {
          .cart-item { flex-direction: column; gap: 1rem; padding: 1rem 0; }
        }

        .item-image-container {
          position: relative;
          width: 100px;
          height: 130px;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .item-image-container {
            width: 100%;
            max-width: 150px;
            height: 200px;
            align-self: center;
          }
        }

        .item-image {
          width: 100%;
          height: 100%;
          border-radius: 8px;
          object-fit: cover;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          cursor: pointer;
          transition: transform 0.3s ease;
        }
        .item-image:hover { transform: scale(1.05); }

        .item-details { flex: 1; min-width: 0; }

        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }
        @media (max-width: 768px) {
          .item-header { flex-direction: column; align-items: flex-start; gap: 0.5rem; }
        }

        .item-title {
          font-weight: 600;
          color: #334155;
          font-size: 1.1rem;
          line-height: 1.3;
          margin: 0;
          cursor: pointer;
          transition: color 0.2s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .item-title:hover { color: #e63946; }

        .item-price {
          font-weight: 700;
          color: #e63946;
          font-size: 1.2rem;
          white-space: nowrap;
        }

        .item-author {
          color: #64748b;
          font-size: 0.9rem;
          margin-bottom: 0.75rem;
        }

        .item-description {
          color: #64748b;
          font-size: 0.85rem;
          line-height: 1.4;
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .price-details {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 10px;
          margin-bottom: 1rem;
          border: 1px solid #e2e8f0;
        }

        .price-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.5rem;
        }

        .mrp-price {
          color: #64748b;
          text-decoration: line-through;
          font-size: 0.95rem;
        }

        .sale-price {
          color: #28a745;
          font-weight: 600;
          font-size: 1rem;
        }

        .discount-badge {
          background: #10b981;
          color: white;
          padding: 0.25rem 0.6rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .price-breakdown {
          font-size: 0.9rem;
          margin: 0.5rem 0;
          padding: 0.5rem;
          background: white;
          border-radius: 6px;
        }

        .price-line {
          display: flex;
          justify-content: space-between;
          padding: 0.25rem 0;
        }

        .gst-detail {
          color: #17a2b8;
          font-weight: 500;
        }

        .final-price {
          font-weight: 700;
          color: #28a745;
          font-size: 1.1rem;
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px dashed #cbd5e1;
        }

        .savings-text {
          color: #10b981;
          font-size: 0.85rem;
          font-weight: 600;
          margin-top: 0.25rem;
        }

        .item-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        @media (max-width: 768px) {
          .item-controls { flex-direction: column; align-items: stretch; gap: 0.75rem; }
        }

        .quantity-control {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: #f8fafc;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .quantity-btn {
          background: none;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          cursor: pointer;
          color: #64748b;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .quantity-btn:hover:not(:disabled) {
          background: #e2e8f0;
          color: #334155;
        }
        .quantity-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .quantity {
          font-weight: 600;
          min-width: 30px;
          text-align: center;
          font-size: 0.95rem;
        }

        .unit-price {
          color: #64748b;
          font-size: 0.85rem;
          white-space: nowrap;
        }

        .item-actions { display: flex; gap: 0.75rem; }
        @media (max-width: 768px) {
          .item-actions { width: 100%; justify-content: space-between; }
        }

        .action-btn {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
        }
        .action-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .remove-btn { background: #fef2f2; color: #dc2626; }
        .remove-btn:hover:not(:disabled) { background: #fecaca; transform: translateY(-1px); }
        .buy-now-btn { background: #dcfce7; color: #166534; }
        .buy-now-btn:hover:not(:disabled) { background: #bbf7d0; transform: translateY(-1px); }

        .order-summary-section {
          background: white;
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: 1px solid #f1f5f9;
          width: 100%;
        }
        @media (min-width: 1024px) {
          .order-summary-section { width: 400px; position: sticky; top: 1.5rem; }
        }
        @media (max-width: 768px) { .order-summary-section { padding: 1.5rem; } }
        @media (max-width: 480px) { .order-summary-section { padding: 1rem; } }

        .summary-header { margin-bottom: 1.5rem; text-align: center; }
        .summary-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          color: #334155;
          margin: 0;
          font-weight: 700;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.95rem;
        }

        .total-row {
          font-size: 1.2rem;
          font-weight: 700;
          color: #e63946;
          padding-top: 1rem;
          border-top: 2px solid #e2e8f0;
          margin-top: 0.5rem;
        }

        .savings-row { color: #10b981; font-size: 0.9rem; }

        .gst-breakdown {
          font-size: 0.85rem;
          color: #64748b;
          padding: 0.5rem 0;
          border-bottom: 1px dashed #e2e8f0;
        }

        .instant-download-banner {
          margin: 1.25rem 0;
          padding: 0.875rem;
          background: linear-gradient(135deg, #dbeafe, #eff6ff);
          border: 1px solid #93c5fd;
          border-radius: 8px;
          font-size: 0.85rem;
          color: #1d4ed8;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-weight: 600;
        }

        .checkout-btn {
          width: 100%;
          padding: 1rem 1.25rem;
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        .checkout-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(230, 57, 70, 0.3);
        }

        .login-prompt {
          text-align: center;
          padding: 1.25rem;
          background: #f0f9ff;
          border-radius: 10px;
          border: 1px solid #bae6fd;
          margin-top: 1rem;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .login-btn {
          width: 100%;
          padding: 1rem 1.25rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .login-btn:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-2px);
        }

        .security-badge {
          margin-top: 1.5rem;
          padding: 1.25rem;
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }

        .currency-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(230, 57, 70, 0.05);
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.85rem;
          color: #64748b;
          margin-bottom: 1rem;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #e63946;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        .spinner.small { width: 12px; height: 12px; border-width: 1px; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <div className="cart-header">
        <button className="back-button" onClick={() => navigate('/')} disabled={loading.refresh}>
          ← Back to Home
        </button>
        <h1 className="cart-title">
          Shopping Cart ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
        </h1>
        <button className="refresh-btn" onClick={() => window.location.reload()} disabled={loading.refresh}>
          {loading.refresh ? <span className="spinner small"></span> : '🔄'} Refresh
        </button>
        <button className="clear-cart-btn" onClick={handleClearCart} disabled={loading.clear}>
          {loading.clear ? <span className="spinner small"></span> : '🗑️'} Clear Cart
        </button>
      </div>

      {currencyData && currencyData[currency] && (
        <div className="currency-info">
          <span>Prices shown in:</span>
          <span className="currency-symbol">
            {currencyData[currency].symbol} {currency} ({currencyData[currency].name})
          </span>
        </div>
      )}

      <div className="cart-layout">
        <div className="cart-items-section">
          {cartItems.map(item => {
            const breakdown = getItemPriceBreakdown(item);
            const isLoading = loading[item.id];
            
            return (
              <div key={item.id} className="cart-item">
                <div className="item-image-container">
                  <img 
                    src={getItemImage(item)} 
                    alt={item.title || item.name}
                    className="item-image"
                    onClick={() => handleProductClick(item)}
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=200&h=300&fit=crop';
                    }}
                  />
                </div>
                <div className="item-details">
                  <div className="item-header">
                    <div>
                      <h3 className="item-title" onClick={() => handleProductClick(item)}>
                        {item.title || item.name}
                      </h3>
                      <div className="item-author">by {item.author}</div>
                    </div>
                    <div className="item-price">
                      {isLoading ? <span className="spinner small"></span> : formatPrice(breakdown.finalPrice)}
                    </div>
                  </div>
                  
                  <div className="item-description">{item.description}</div>

                  <div className="price-details">
                    <div className="price-row">
                      <span className="mrp-price">MRP: {formatPrice(breakdown.mrp)}</span>
                      {breakdown.salePrice > 0 && (
                        <span className="sale-price">Sale: {formatPrice(breakdown.salePrice)}</span>
                      )}
                      {breakdown.effectiveDiscount > 0 && (
                        <span className="discount-badge">{breakdown.effectiveDiscount}% OFF</span>
                      )}
                    </div>
                    
                    <div className="price-breakdown">
                      <div className="price-line">
                        <span>Base Price:</span>
                        <span>{formatPrice(breakdown.basePrice)}</span>
                      </div>
                      {breakdown.effectiveDiscount > 0 && (
                        <div className="price-line">
                          <span>Discount ({breakdown.effectiveDiscount}%):</span>
                          <span>-{formatPrice(breakdown.basePrice - breakdown.discountedPrice)}</span>
                        </div>
                      )}
                      <div className="price-line">
                        <span>Discounted Price:</span>
                        <span>{formatPrice(breakdown.discountedPrice)}</span>
                      </div>
                      {!breakdown.gstIncluded && (
                        <div className="price-line gst-detail">
                          <span>GST (18%):</span>
                          <span>+{formatPrice(breakdown.gstAmount)}</span>
                        </div>
                      )}
                      <div className="price-line final-price">
                        <span><strong>Final Price:</strong></span>
                        <span><strong>{formatPrice(breakdown.finalPrice)}</strong></span>
                      </div>
                    </div>

                    <div className="savings-text">
                      You save {formatPrice(breakdown.savings)} ({breakdown.savingsPercentage}% discount applied)
                    </div>
                    {breakdown.gstIncluded && (
                      <div style={{ fontSize: '0.75rem', color: '#17a2b8', marginTop: '4px' }}>
                        GST included in price
                      </div>
                    )}
                  </div>

                  <div className="item-controls">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <div className="quantity-control">
                        <button 
                          className="quantity-btn" 
                          onClick={() => handleUpdateQuantity(item.id, (item.quantity || 1) - 1)}
                          disabled={isLoading || (item.quantity || 1) <= 1}
                        >−</button>
                        <span className="quantity">{isLoading ? '...' : (item.quantity || 1)}</span>
                        <button 
                          className="quantity-btn" 
                          onClick={() => handleUpdateQuantity(item.id, (item.quantity || 1) + 1)}
                          disabled={isLoading}
                        >+</button>
                      </div>
                      <div className="unit-price">
                        {formatPrice(breakdown.perItem.finalPrice)} each
                      </div>
                    </div>
                    <div className="item-actions">
                      <button 
                        className="action-btn buy-now-btn"
                        onClick={() => handleBuyNow(item)}
                        disabled={isLoading}
                      >
                        Buy Now
                      </button>
                      <button 
                        className="action-btn remove-btn" 
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={isLoading}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="order-summary-section">
          <div className="summary-header">
            <h3 className="summary-title">Order Summary</h3>
          </div>

          <div className="instant-download-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Instant Digital Download After Purchase
          </div>

          <div>
            <div className="summary-item">
              <span>MRP Total</span>
              <span>{formatPrice(totals.mrp)}</span>
            </div>
            <div className="summary-item">
              <span>Cart Total</span>
              <span>{formatPrice(totals.subtotal)}</span>
            </div>
            <div className="gst-breakdown">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>GST Breakdown (18%):</span>
                <span>{formatPrice(totals.gst)}</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                (GST is added to discounted price)
              </div>
            </div>
            {totals.savings > 0 && (
              <div className="summary-item savings-row">
                <span>Total Savings</span>
                <span>-{formatPrice(totals.savings)}</span>
              </div>
            )}
            <div className="summary-item total-row">
              <span>Total Amount (incl. GST)</span>
              <span>{formatPrice(totals.total)}</span>
            </div>
          </div>

          <div style={{ margin: '1rem 0', fontSize: '0.85rem', color: '#64748b', textAlign: 'center' }}>
            <strong>Note:</strong> All books are digital downloads. No shipping required!
          </div>

          {showLoginPrompt && (
            <div className="login-prompt">
              <p>Please log in to complete your purchase</p>
              <button className="login-btn" onClick={() => navigate('/login')}>
                Login to Checkout
              </button>
            </div>
          )}

          <button 
            className="checkout-btn" 
            onClick={handleProceedToCheckout}
            disabled={cartItems.length === 0}
          >
            Proceed to Checkout
          </button>

          <div className="security-badge">
            <div className="security-header">
              <div style={{ color: '#22c55e' }}><ShieldIcon /></div>
              <div style={{ fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>Secure Checkout</div>
            </div>
            <div className="security-text">
              Your payment information is encrypted and secure. We don't share your details with anyone.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;